import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Download, Loader2, Edit2, Check, AlertTriangle, TrendingUp, Droplets } from 'lucide-react';
import * as XLSX from 'xlsx';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const SHIFT_LABELS = { A: '1st Shift', B: '2nd Shift', C: '3rd Shift' };

export default function DailyReportTab({ selectedDate }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingStoppages, setEditingStoppages] = useState(false);
  const [stoppageForm, setStoppageForm] = useState({ A: '', B: '', C: '' });

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/production/daily-summary?date=${selectedDate}`, { headers: authHeaders() });
      setSummary(res.data);
      // Initialise stoppage form from loaded data
      const sf = { A: '', B: '', C: '' };
      (res.data.stoppages || []).forEach(s => { sf[s.shift] = s.reason || ''; });
      setStoppageForm(sf);
    } catch {
      toast.error('Failed to load daily summary');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const saveStoppages = async () => {
    try {
      const stoppages = ['A', 'B', 'C'].map(s => ({ shift: s, reason: stoppageForm[s] || 'Nil', hours: 0 }));
      await axios.put(`${API}/production/stoppages`, { date: selectedDate, stoppages }, { headers: authHeaders() });
      toast.success('Stoppages saved');
      setEditingStoppages(false);
      fetchSummary();
    } catch { toast.error('Failed to save stoppages'); }
  };

  // ── Diesel calculations ────────────────────────────────────────────────────
  const calcDieselEfficiency = () => {
    if (!summary) return null;
    const { daily, monthly, diesel, material_types: matTypes } = summary;

    const obMats = matTypes.filter(m => m.toLowerCase().includes('overburden') || m === 'OB');
    const coalShaleMats = matTypes.filter(m => !obMats.includes(m));

    const sumTrips = (mats, src) => mats.reduce((a, m) => a + (src[m]?.trips || 0), 0);
    const sumVol = (mats, src) => mats.reduce((a, m) => a + (src[m]?.volume || 0), 0);

    const dayOBTrips = sumTrips(obMats, daily.totals);
    const dayOBVol = sumVol(obMats, daily.totals);
    const dayCSTrips = sumTrips(coalShaleMats, daily.totals);
    const dayCSVol = sumVol(coalShaleMats, daily.totals);

    const dayOBElig = round2(dayOBVol * diesel.ob_factor);
    const dayCSElig = round2(dayCSVol * diesel.coal_shale_factor);
    const dayTotalElig = round2(dayOBElig + dayCSElig);
    const dayVariance = round2(dayTotalElig - diesel.daily_consumed);

    const mthOBVol = sumVol(obMats, monthly.totals);
    const mthCSVol = sumVol(coalShaleMats, monthly.totals);
    const mthTotalElig = round2(mthOBVol * diesel.ob_factor + mthCSVol * diesel.coal_shale_factor);
    const mthVariance = round2(mthTotalElig - diesel.monthly_consumed);

    const mthTotalVol = round2(mthOBVol + mthCSVol);
    const mthTotalTrips = monthly.grand_total.trips;

    return {
      daily: {
        obTrips: dayOBTrips, obVol: dayOBVol, obFactor: diesel.ob_factor, obElig: dayOBElig,
        csTrips: dayCSTrips, csVol: dayCSVol, csFactor: diesel.coal_shale_factor, csElig: dayCSElig,
        totalTrips: daily.grand_total.trips, totalVol: daily.grand_total.volume,
        totalElig: dayTotalElig, consumed: diesel.daily_consumed, variance: dayVariance,
        eligFactor: daily.grand_total.volume > 0 ? round2(dayTotalElig / daily.grand_total.volume) : 0,
        consumedFactor: daily.grand_total.volume > 0 ? round2(diesel.daily_consumed / daily.grand_total.volume) : 0,
      },
      monthly: {
        obVol: mthOBVol, csVol: mthCSVol, totalVol: mthTotalVol, totalTrips: mthTotalTrips,
        totalElig: mthTotalElig, consumed: diesel.monthly_consumed, variance: mthVariance,
        eligFactor: mthTotalVol > 0 ? round2(mthTotalElig / mthTotalVol) : 0,
        consumedFactor: mthTotalVol > 0 ? round2(diesel.monthly_consumed / mthTotalVol) : 0,
      },
    };
  };

  const round2 = n => Math.round(n * 100) / 100;
  const fmt = n => typeof n === 'number' ? n.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : n;

  // ── Excel Export ───────────────────────────────────────────────────────────
  const exportExcel = () => {
    if (!summary) return;
    const { daily, monthly, material_types: mats, sccl_factors, diesel } = summary;
    const de = calcDieselEfficiency();
    const wb = XLSX.utils.book_new();

    // Sheet 1 — Production Summary
    const hdr = ['Date', 'Shift', ...mats, 'Total Trips', 'Volume (m³)'];
    const rows = [hdr];
    const dateLabel = selectedDate;
    ['A', 'B', 'C'].forEach(s => {
      const row = [s === 'A' ? dateLabel : '', SHIFT_LABELS[s]];
      mats.forEach(m => row.push(daily.by_shift[s]?.[m]?.trips || 0));
      row.push(mats.reduce((a, m) => a + (daily.by_shift[s]?.[m]?.trips || 0), 0));
      row.push(round2(mats.reduce((a, m) => a + (daily.by_shift[s]?.[m]?.volume || 0), 0)));
      rows.push(row);
    });
    // Totals
    const totRow = ['Total', ''];
    mats.forEach(m => totRow.push(daily.totals[m]?.trips || 0));
    totRow.push(daily.grand_total.trips); totRow.push(daily.grand_total.volume);
    rows.push(totRow);
    // Volume
    const volRow = ['Quantity', ''];
    mats.forEach(m => volRow.push(round2(daily.totals[m]?.volume || 0)));
    volRow.push(''); volRow.push(daily.grand_total.volume);
    rows.push(volRow);
    // Monthly Cumulative
    const cumRow = ['Monthly Cumulative', ''];
    mats.forEach(m => cumRow.push(round2(monthly.totals[m]?.volume || 0)));
    cumRow.push(''); cumRow.push(monthly.grand_total.volume);
    rows.push(cumRow);
    // Stoppages
    rows.push([]);
    rows.push(['Work Stoppages', 'Shift', 'Reason']);
    ['A', 'B', 'C'].forEach(s => {
      const st = summary.stoppages?.find(x => x.shift === s);
      rows.push(['', SHIFT_LABELS[s], st?.reason || 'Nil']);
    });

    // SCCL factors — separate block
    if (sccl_factors && Object.keys(sccl_factors).length > 0) {
      rows.push([]);
      rows.push(['SCCL Report Factors (cum/trip)']);
      mats.forEach(m => rows.push([m, sccl_factors[m] || '—']));
    }

    const ws1 = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws1, 'Production');

    // Sheet 2 — Diesel Efficiency
    if (de) {
      const d2 = [
        [`DETAILS OF DIESEL CONSUMPTION ON ${selectedDate}`],
        [],
        ['DESCRIPTION', 'OB', 'COAL & SHALE', 'TOTAL'],
        ['TRIPS', de.daily.obTrips, de.daily.csTrips, de.daily.totalTrips],
        ['APPROX QUANTITY (m³)', fmt(de.daily.obVol), fmt(de.daily.csVol), fmt(de.daily.totalVol)],
        ['DIESEL FACTOR', diesel.ob_factor, diesel.coal_shale_factor, '-'],
        ['ELIGIBILITY DIESEL (L)', fmt(de.daily.obElig), fmt(de.daily.csElig), fmt(de.daily.totalElig)],
        ['CONSUMED DIESEL (L)', fmt(diesel.daily_consumed), '', de.daily.variance >= 0 ? fmt(de.daily.variance) + ' SAVED' : fmt(Math.abs(de.daily.variance)) + ' EXCESS'],
        ['ELIGIBILITY FACTOR', de.daily.eligFactor, 'CONSUMED FACTOR', de.daily.consumedFactor],
        [],
        ['CUMULATIVE (Month-to-Date)'],
        ['CUMULATIVE QTY. (m³)', fmt(de.monthly.obVol), fmt(de.monthly.csVol), fmt(de.monthly.totalVol)],
        ['ELIGIBILITY DIESEL (L)', '', '', fmt(de.monthly.totalElig)],
        ['CONSUMED DIESEL (L)', fmt(diesel.monthly_consumed), '', de.monthly.variance >= 0 ? fmt(de.monthly.variance) + ' SAVED' : fmt(Math.abs(de.monthly.variance)) + ' EXCESS'],
        ['ELIGIBILITY FACTOR', de.monthly.eligFactor, 'CONSUMED FACTOR', de.monthly.consumedFactor],
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(d2);
      XLSX.utils.book_append_sheet(wb, ws2, 'Diesel Efficiency');
    }

    XLSX.writeFile(wb, `Daily_Report_${selectedDate}.xlsx`);
    toast.success('Report downloaded!');
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  if (!summary) return null;

  const { daily, monthly, material_types: mats, stoppages } = summary;
  const de = calcDieselEfficiency();

  return (
    <div className="space-y-6" data-testid="daily-report-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-800">Daily Operations Report</h3>
          <p className="text-sm text-slate-500 mt-0.5">{selectedDate} — Production + Diesel Efficiency</p>
        </div>
        <Button onClick={exportExcel} variant="outline" size="sm" data-testid="export-daily-report-btn">
          <Download className="h-4 w-4 mr-2" />
          Download Excel
        </Button>
      </div>

      {/* Section A — Production Summary */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="bg-slate-800 text-white px-4 py-2.5 flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          <span className="font-semibold text-sm">Production Summary — {selectedDate}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-100 border-b">
                <th className="text-left px-4 py-2.5 text-slate-600 font-semibold w-32">Date / Context</th>
                <th className="text-center px-3 py-2.5 text-slate-600 font-semibold w-28">Shift</th>
                {mats.map(m => (
                  <th key={m} className="text-right px-3 py-2.5 text-slate-600 font-semibold">{m}</th>
                ))}
                <th className="text-right px-4 py-2.5 text-slate-800 font-bold">Total Trips</th>
                <th className="text-right px-4 py-2.5 text-slate-800 font-bold">Volume (m³)</th>
              </tr>
            </thead>
            <tbody>
              {['A', 'B', 'C'].map((shift, idx) => {
                const rowTrips = mats.reduce((a, m) => a + (daily.by_shift[shift]?.[m]?.trips || 0), 0);
                const rowVol = round2(mats.reduce((a, m) => a + (daily.by_shift[shift]?.[m]?.volume || 0), 0));
                return (
                  <tr key={shift} className="border-b hover:bg-slate-50">
                    {idx === 0 && (
                      <td className="px-4 py-2.5 font-semibold text-orange-700 bg-orange-50" rowSpan={3}>
                        {selectedDate}<br />
                        <span className="text-xs font-normal text-orange-500">Trips</span>
                      </td>
                    )}
                    <td className="px-3 py-2.5 text-center bg-amber-50 font-medium text-amber-800">{SHIFT_LABELS[shift]}</td>
                    {mats.map(m => (
                      <td key={m} className="px-3 py-2.5 text-right">{fmt(daily.by_shift[shift]?.[m]?.trips || 0)}</td>
                    ))}
                    <td className="px-4 py-2.5 text-right font-semibold">{fmt(rowTrips)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-500">{fmt(rowVol)}</td>
                  </tr>
                );
              })}
              {/* Total row */}
              <tr className="bg-pink-50 border-b font-semibold">
                <td className="px-4 py-2.5 text-pink-800 font-bold" colSpan={2}>Total</td>
                {mats.map(m => (
                  <td key={m} className="px-3 py-2.5 text-right text-slate-800">{fmt(daily.totals[m]?.trips || 0)}</td>
                ))}
                <td className="px-4 py-2.5 text-right font-bold text-slate-900">{fmt(daily.grand_total.trips)}</td>
                <td className="px-4 py-2.5 text-right font-bold text-slate-900">{fmt(daily.grand_total.volume)}</td>
              </tr>
              {/* Volume row */}
              <tr className="bg-pink-50 border-b">
                <td className="px-4 py-2.5 text-pink-800 font-semibold" colSpan={2}>Quantity (m³)</td>
                {mats.map(m => (
                  <td key={m} className="px-3 py-2.5 text-right text-slate-700">{fmt(round2(daily.totals[m]?.volume || 0))}</td>
                ))}
                <td className="px-4 py-2.5 text-right"></td>
                <td className="px-4 py-2.5 text-right font-semibold">{fmt(daily.grand_total.volume)}</td>
              </tr>
              {/* Monthly Cumulative */}
              <tr className="bg-slate-200 font-semibold">
                <td className="px-4 py-2.5 text-slate-700 font-bold" colSpan={2}>Monthly Cumulative</td>
                {mats.map(m => (
                  <td key={m} className="px-3 py-2.5 text-right">{fmt(round2(monthly.totals[m]?.volume || 0))}</td>
                ))}
                <td className="px-4 py-2.5 text-right text-slate-500">{fmt(monthly.grand_total.trips)} trips</td>
                <td className="px-4 py-2.5 text-right font-bold">{fmt(monthly.grand_total.volume)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Work Stoppages */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 bg-orange-50 border-b">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-500" />
            <span className="font-semibold text-sm text-orange-800">Work Stoppages</span>
          </div>
          {editingStoppages ? (
            <Button size="sm" onClick={saveStoppages} className="h-7 bg-slate-900 hover:bg-slate-800">
              <Check className="h-3.5 w-3.5 mr-1" /> Save
            </Button>
          ) : (
            <Button variant="ghost" size="sm" className="h-7 text-orange-700" onClick={() => setEditingStoppages(true)}>
              <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
            </Button>
          )}
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-slate-50">
              <th className="text-left px-4 py-2 text-slate-600 font-semibold w-40">Context</th>
              <th className="text-center px-4 py-2 text-slate-600 font-semibold w-32">Shift</th>
              <th className="text-left px-4 py-2 text-slate-600 font-semibold">Reason / Remarks</th>
            </tr>
          </thead>
          <tbody>
            {['A', 'B', 'C'].map((shift, idx) => {
              const st = stoppages?.find(s => s.shift === shift);
              return (
                <tr key={shift} className="border-b">
                  {idx === 0 && <td className="px-4 py-2 text-orange-700 font-semibold bg-orange-50 align-top" rowSpan={3}>Work<br />Stopped</td>}
                  <td className="px-4 py-2 text-center bg-amber-50 font-medium text-amber-800">{SHIFT_LABELS[shift]}</td>
                  <td className="px-4 py-2">
                    {editingStoppages ? (
                      <Input
                        value={stoppageForm[shift]}
                        onChange={e => setStoppageForm(f => ({ ...f, [shift]: e.target.value }))}
                        placeholder="e.g. Fog, Blasting, Nil"
                        className="h-7 text-sm"
                        data-testid={`stoppage-input-${shift}`}
                      />
                    ) : (
                      <span className={st?.reason && st.reason !== 'Nil' ? 'text-amber-700 font-medium' : 'text-slate-400'}>
                        {st?.reason || 'Nil'}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Section B — Diesel Efficiency */}
      {de && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="bg-blue-900 text-white px-4 py-2.5 flex items-center gap-2">
            <Droplets className="h-4 w-4" />
            <span className="font-semibold text-sm">Diesel Efficiency Analysis — {selectedDate}</span>
          </div>

          {/* Daily diesel table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-50 border-b">
                  <th className="text-left px-4 py-2.5 font-semibold text-blue-900 w-52">Description</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-blue-900">OB</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-blue-900">Coal &amp; Shale</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-blue-900">Total</th>
                </tr>
              </thead>
              <tbody>
                <DieselRow label="TRIPS" ob={de.daily.obTrips} cs={de.daily.csTrips} total={de.daily.totalTrips} />
                <DieselRow label="APPROX QUANTITY (m³)" ob={de.daily.obVol} cs={de.daily.csVol} total={de.daily.totalVol} fmt={fmt} />
                <DieselRow label="DIESEL FACTOR" ob={de.daily.obFactor} cs={de.daily.csFactor} total="—" />
                <DieselRow label="ELIGIBILITY DIESEL (L)" ob={de.daily.obElig} cs={de.daily.csElig} total={de.daily.totalElig} fmt={fmt} highlight="blue" />
                {/* Consumed diesel spans OB+CS */}
                <tr className="border-b bg-orange-50">
                  <td className="px-4 py-2.5 font-semibold text-orange-900">CONSUMED DIESEL (L)</td>
                  <td colSpan={2} className="px-4 py-2.5 text-right font-semibold">{fmt(de.daily.consumed)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <VarianceBadge variance={de.daily.variance} />
                  </td>
                </tr>
                <tr className="border-b bg-green-50">
                  <td className="px-4 py-2.5 font-semibold text-green-900">ELIGIBILITY FACTOR</td>
                  <td className="px-4 py-2.5 text-right">{de.daily.eligFactor}</td>
                  <td className="px-4 py-2.5 text-right text-slate-600 font-medium">CONSUMED FACTOR</td>
                  <td className="px-4 py-2.5 text-right">{de.daily.consumedFactor}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Monthly cumulative diesel */}
          <div className="border-t-2 border-blue-200 overflow-x-auto">
            <div className="px-4 py-2 bg-blue-50 font-semibold text-blue-800 text-xs uppercase tracking-wide">Monthly Cumulative</div>
            <table className="w-full text-sm">
              <tbody>
                <DieselRow label="CUMULATIVE QTY. (m³)" ob={de.monthly.obVol} cs={de.monthly.csVol} total={de.monthly.totalVol} fmt={fmt} />
                <DieselRow label="ELIGIBILITY DIESEL (L)" ob="—" cs="—" total={de.monthly.totalElig} fmt={fmt} highlight="blue" />
                <tr className="border-b bg-orange-50">
                  <td className="px-4 py-2.5 font-semibold text-orange-900">CONSUMED DIESEL (L)</td>
                  <td colSpan={2} className="px-4 py-2.5 text-right font-semibold">{fmt(de.monthly.consumed)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <VarianceBadge variance={de.monthly.variance} />
                  </td>
                </tr>
                <tr className="border-b bg-green-50">
                  <td className="px-4 py-2.5 font-semibold text-green-900">ELIGIBILITY FACTOR</td>
                  <td className="px-4 py-2.5 text-right">{de.monthly.eligFactor}</td>
                  <td className="px-4 py-2.5 text-right text-slate-600 font-medium">CONSUMED FACTOR</td>
                  <td className="px-4 py-2.5 text-right">{de.monthly.consumedFactor}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {de?.daily.consumed === 0 && (
        <p className="text-xs text-slate-400 text-center mt-2">
          No diesel entries found for {selectedDate}. Add diesel records in the Diesel module to see efficiency analysis.
        </p>
      )}
    </div>
  );
}

function DieselRow({ label, ob, cs, total, fmt: fmtFn, highlight }) {
  const f = fmtFn || (x => x);
  const bg = highlight === 'blue' ? 'bg-blue-50' : 'hover:bg-slate-50';
  return (
    <tr className={`border-b ${bg}`}>
      <td className="px-4 py-2.5 font-semibold text-slate-700">{label}</td>
      <td className="px-4 py-2.5 text-right">{typeof ob === 'number' ? f(ob) : ob}</td>
      <td className="px-4 py-2.5 text-right">{typeof cs === 'number' ? f(cs) : cs}</td>
      <td className="px-4 py-2.5 text-right font-semibold">{typeof total === 'number' ? f(total) : total}</td>
    </tr>
  );
}

function VarianceBadge({ variance }) {
  if (variance === 0) return <span className="text-slate-500">0</span>;
  if (variance > 0) {
    return <span className="inline-flex items-center gap-1 text-green-700 font-bold">+{variance.toLocaleString('en-IN', { maximumFractionDigits: 2 })} <span className="text-xs bg-green-100 px-1.5 py-0.5 rounded">SAVED</span></span>;
  }
  return <span className="inline-flex items-center gap-1 text-red-700 font-bold">{Math.abs(variance).toLocaleString('en-IN', { maximumFractionDigits: 2 })} <span className="text-xs bg-red-100 px-1.5 py-0.5 rounded">EXCESS</span></span>;
}
